<template>
  <div class="ja-daily-trend">
    <div class="ja-daily-trend__header">
      <div class="ja-daily-trend__intro">
        <h3 class="ja-daily-trend__title">Daily Activity Trend</h3>
        <p class="ja-daily-trend__subtitle">Events occurring during selected dates</p>
      </div>
      <div class="ja-daily-trend__legend">
        <div
          v-for="item in legendItems"
          :key="item.name"
          class="ja-daily-trend__legend-item"
        >
          <span class="ja-daily-trend__legend-dot" :style="{ background: item.color }" />
          <span>{{ item.name }}</span>
        </div>
      </div>
    </div>
    <ja-apex-chart
      type="line"
      :height="320"
      :options="chartOptions"
      :series="series"
    />
  </div>
</template>

<script>
import JaApexChart from "./JaApexChart.vue";

const LEGEND_ITEMS = [
  { name: "Opens", color: "#8B5CF6" },
  { name: "Pay Now Clicks", color: "#6366F1" },
  { name: "Payments", color: "#16A34A" },
];

export default {
  name: "DailyActivityTrend",
  components: { JaApexChart },
  props: {
    categories: { type: Array, required: true },
    opens: { type: Array, required: true },
    payNowClicks: { type: Array, required: true },
    payments: { type: Array, required: true },
  },
  data() {
    return {
      legendItems: LEGEND_ITEMS,
    };
  },
  computed: {
    series() {
      return [
        { name: "Opens", data: this.opens },
        { name: "Pay Now Clicks", data: this.payNowClicks },
        { name: "Payments", data: this.payments },
      ];
    },
    chartOptions() {
      const allValues = [
        ...this.opens,
        ...this.payNowClicks,
        ...this.payments,
      ].map((value) => Number(value || 0));
      const maxValue = allValues.length ? Math.max(...allValues, 0) : 0;
      const yMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 10;

      return {
        chart: {
          toolbar: { show: false },
          fontFamily: "inherit",
        },
        colors: LEGEND_ITEMS.map((item) => item.color),
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 4, strokeWidth: 0 },
        xaxis: {
          categories: this.categories,
          labels: { style: { colors: "#6e6b7b" } },
        },
        yaxis: {
          min: 0,
          max: yMax,
          tickAmount: 5,
          labels: {
            style: { colors: "#6e6b7b" },
            formatter: (val) => Math.round(val).toLocaleString(),
          },
        },
        legend: { show: false },
        grid: {
          borderColor: "#f3f2f7",
          strokeDashArray: 4,
          padding: {
            top: 4,
            right: 8,
          },
        },
        tooltip: {
          y: { formatter: (val) => Number(val || 0).toLocaleString() },
        },
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ja-daily-trend {
  background: #fff;
  border-radius: 18px;
  padding: 1.35rem 1.5rem 1.25rem;
  box-shadow: 0 1px 2px 0 rgba(20, 21, 34, 0.04);
  border: 1px solid #ececf2;
  height: 100%;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  &__intro {
    min-width: 0;
  }

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.3rem;
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #a8a8b3;
    margin: 0;
    line-height: 1.4;
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.85rem 1rem;
    flex-shrink: 0;
    padding-top: 0.15rem;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
    line-height: 1;
  }

  &__legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .ja-daily-trend__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .ja-daily-trend__legend {
    justify-content: flex-start;
  }
}
</style>
